import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { BlogPost } from "@/utils/blogUtils";
import { ArrowLeft, User, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/utils/analytics";

interface BlogPostHeaderProps {
  post: BlogPost;
  handleTagClick: (tag: string, event: React.MouseEvent) => void;
  readTime: string;
}

const BlogPostHeader = ({ post, handleTagClick, readTime }: BlogPostHeaderProps) => {
  return (
    <header className="mb-10">
      {/* Featured image - Full width with appropriate dimensions */}
      <div className="w-full mb-10 rounded-xl overflow-hidden shadow-md">
        <img 
          src={post.image} 
          alt={post.title} 
          className="w-full h-auto object-cover" 
          style={{ maxHeight: '500px' }}
          width="1200"
          height="630"
          loading="eager"
        />
      </div>
    
      <div className="max-w-4xl mx-auto">
        {/* Post tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags.map((tag) => (
            <Badge 
              key={tag} 
              variant="secondary"
              className="bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-800/40 px-3 py-1 text-sm font-medium shadow-sm"
              onClick={(e) => handleTagClick(tag, e)}
            >
              {tag}
            </Badge>
          ))}
        </div>
        
        {/* Post title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">{post.title}</h1>
        
        {/* Post metadata (author, date, read time) */}
        <div className="flex flex-wrap items-center gap-6 text-gray-600 dark:text-gray-400 text-sm border-b border-gray-200 dark:border-gray-800 pb-6">
          <div className="flex items-center">
            <User size={16} className="mr-2" />
            <span>{post.author}</span>
          </div>
          <div className="flex items-center">
            <Calendar size={16} className="mr-2" />
            <span>
              {new Date(post.publish_date).toLocaleDateString('pt-BR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
          <div className="flex items-center">
            <Clock size={16} className="mr-2" />
            <span>{readTime} min de leitura</span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default BlogPostHeader;
